import {Taillights} from '@github-ui/taillights'
import {useTaillightsController, useTaillightsRendering} from '@github-ui/taillights/provider'
import {taillightsRenderingStyle} from '@github-ui/taillights/rendering-styles'
import type {PreparedView, SourceRange, SourceSelection, SourceSymbol} from '@github-ui/taillights-worker/document'
import {testIdProps} from '@github-ui/test-id-props'
import {ScopedCommands} from '@github-ui/ui-commands'
import {clsx} from 'clsx'
import {
  type FocusEventHandler,
  type KeyboardEventHandler,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'

import {getNativeSelection, getSourceSelection, type NativeSelectionMap} from './native-selection'
import styles from './taillights-symbols.module.css'
import {useRangeDecorations} from './use-range-decorations'
import {useSymbolSearch} from './use-symbol-search'

export type TaillightsSourceSelection = SourceSelection
export type TaillightsSymbol = SourceSymbol
export type TaillightsSymbolMatch = Omit<SourceSymbol, 'text'>

export type TaillightsSymbolsHandle = {
  inspectSelection: () => void
  focus: () => void
  getSelection: () => SourceSelection | null
  setSelection: (selection: SourceSelection) => boolean
}

export type SymbolInteractionHandle = TaillightsSymbolsHandle
const NO_FOLDS: readonly number[] = []

export type TaillightsSymbolsProps = {
  'aria-label': string
  view: PreparedView
  highlightRanges?: readonly SourceRange[]
  focusedHighlightRange?: SourceRange | null
  onSelectionChange?: (selection: SourceSelection | null) => void
  onFocus?: FocusEventHandler<HTMLTextAreaElement>
  onBlur?: FocusEventHandler<HTMLTextAreaElement>
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>
  onSymbolSelected?: (symbol: SourceSymbol) => void
  onRenderError?: (error: Error) => void
  ref?: Ref<TaillightsSymbolsHandle>
  textareaRef?: Ref<HTMLTextAreaElement>
  children?: ReactNode
}

export type TaillightsSourceInteractionProps = Omit<TaillightsSymbolsProps, 'view'> & {
  view: PreparedView | null
  fallbackText?: string
  hideNativeCaret?: boolean
  initialProjection?: NativeSelectionMap
  hasPlaintextPaint?: boolean
}

/** Keeps native selection and keyboard navigation mounted independently of paint and passive decorations. */
export function TaillightsSourceInteraction({
  'aria-label': ariaLabel,
  view,
  fallbackText,
  hideNativeCaret = false,
  initialProjection,
  hasPlaintextPaint = false,
  focusedHighlightRange,
  highlightRanges,
  onBlur,
  onFocus,
  onKeyDown,
  onSelectionChange,
  onSymbolSelected,
  onRenderError,
  ref,
  textareaRef: externalTextareaRef,
  children,
}: TaillightsSourceInteractionProps) {
  if (!view && !initialProjection && fallbackText === undefined)
    throw new Error('Unprepared source requires fallbackText.')
  const controller = useTaillightsController()
  const rendering = useTaillightsRendering()
  const projection = view ?? initialProjection
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const getSelection = useCallback(() => {
    const textarea = textareaRef.current
    return textarea && projection
      ? getSourceSelection(projection, textarea.selectionStart, textarea.selectionEnd, textarea.selectionDirection)
      : null
  }, [projection])
  const {inspectSelection} = useSymbolSearch({getSelection, onSymbolSelected, onRenderError})
  const {controlledRects, focusedRects} = useRangeDecorations({
    collapsed: view?.collapsed ?? NO_FOLDS,
    enabled: view !== null,
    highlightRanges,
    focusedHighlightRange,
    onRenderError,
  })

  const attachTextarea = useCallback(
    (node: HTMLTextAreaElement) => {
      textareaRef.current = node
      const cleanup = attachExternalRef(externalTextareaRef, node)
      return () => {
        textareaRef.current = null
        cleanup()
      }
    },
    [externalTextareaRef],
  )

  useEffect(() => {
    const native = view?.nativeSelection
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Native selection must be applied after the projected textarea value commits.
    if (native) textareaRef.current?.setSelectionRange(native.start, native.end, native.direction)
  }, [view?.nativeSelection])

  useImperativeHandle(
    ref,
    () => ({
      inspectSelection,
      focus: () => textareaRef.current?.focus(),
      getSelection,
      setSelection(selection) {
        const textarea = textareaRef.current
        const native = projection && getNativeSelection(projection, selection)
        if (!textarea || !native) return false
        textarea.setSelectionRange(native.start, native.end, native.direction)
        onSelectionChange?.(getSelection())
        return true
      },
    }),
    [getSelection, inspectSelection, onSelectionChange, projection],
  )

  // A controlled value ignores edits without native readOnly disabling caret navigation in Safari.
  return (
    <>
      <div aria-hidden className={styles.BackgroundOverlay}>
        {controlledRects.map(rect => (
          <span
            key={`${rect.line}-${rect.startColumn}-${rect.endColumn}`}
            className={styles.ControlledHighlight}
            {...testIdProps('taillights-controlled-highlight')}
            style={{height: rect.height, left: rect.x, top: rect.y, width: rect.width}}
          />
        ))}
        {focusedRects.map(rect => (
          <span
            key={`${rect.line}-${rect.startColumn}-${rect.endColumn}`}
            className={styles.FocusedHighlight}
            {...testIdProps('taillights-focused-highlight')}
            style={{height: rect.height, left: rect.x, top: rect.y, width: rect.width}}
          />
        ))}
      </div>
      <ScopedCommands
        className={styles.TextareaLayer}
        commands={{'code-view:inspect-symbol': view ? inspectSelection : undefined}}
      >
        <textarea
          ref={attachTextarea}
          aria-label={ariaLabel}
          aria-readonly
          className={clsx(
            styles.Textarea,
            !view && !hasPlaintextPaint && styles.plainText,
            hideNativeCaret && styles.hiddenCaret,
          )}
          {...testIdProps('taillights-symbol-textarea')}
          inputMode="none"
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onMouseUp={event => {
            if (view && event.button === 0 && onSymbolSelected) inspectSelection()
          }}
          onChange={() => {}}
          onSelect={() => onSelectionChange?.(getSelection())}
          spellCheck={false}
          style={{tabSize: controller?.profile.tabSize ?? rendering?.tabSize ?? 4}}
          value={projection?.text ?? fallbackText}
        />
      </ScopedCommands>
      {children}
    </>
  )
}

/** Combines retained document paint with one persistent native interaction surface. */
export function TaillightsSymbols({children, ...props}: TaillightsSymbolsProps) {
  const controller = useTaillightsController()
  const rendering = useTaillightsRendering()
  return (
    <div className={styles.Source} style={taillightsRenderingStyle(controller?.profile ?? rendering)}>
      <div className={styles.SourceContent}>
        <TaillightsSourceInteraction {...props}>
          {/* Keep syntax above native selection without intercepting clicks or disabling child controls. */}
          <div className={styles.PaintLayer} inert aria-hidden {...testIdProps('taillights-paint-layer')}>
            <Taillights view={props.view} inert onRenderError={props.onRenderError} />
          </div>
        </TaillightsSourceInteraction>
      </div>
      {children}
    </div>
  )
}

function attachExternalRef(ref: Ref<HTMLTextAreaElement> | undefined, node: HTMLTextAreaElement) {
  if (typeof ref === 'function') {
    const cleanup = ref(node)
    return () => {
      if (typeof cleanup === 'function') cleanup()
      else ref(null)
    }
  }
  if (ref) ref.current = node
  return () => {
    if (ref) ref.current = null
  }
}
